<template>
  <div class="min-h-[160px]">
    <div
      v-if="parsed"
      class="rounded-12 bg-bg-base-bg1 p-3 text-label-r-13-18"
      data-test="vcard-in-message"
    >
      <div class="flex items-center gap-3">
        <contact-avatar
          :name="parsed.name"
          :contact="contact"
          :size="40"
          class="shrink-0"
        />
        <div class="min-w-0 grow">
          <p class="m-0 truncate text-label-m-13-18 text-fg-base-fg3">
            {{ parsed.name }}
          </p>
          <p
            v-if="parsed.title"
            class="m-0 truncate text-label-r-12-18 text-fg-base-fg17"
          >
            {{ parsed.title }}
          </p>
        </div>
      </div>

      <div class="mt-2">
        <p
          v-if="parsed.org"
          class="mb-0.5 text-fg-base-fg22"
        >
          {{ parsed.org }}
        </p>
        <p
          v-for="tel in parsed.mobileTel.slice(0, 2)"
          :key="'m-' + tel"
          class="mb-1 text-fg-base-fg22"
        >
          {{ $t('common.mobilePhone') }}: {{ tel }}
        </p>
        <p
          v-for="tel in parsed.tel.slice(0, 2)"
          :key="tel"
          class="mb-1 text-fg-base-fg22"
        >
          {{ $t('common.phoneNumber') }}: {{ tel }}
        </p>
        <p
          v-for="email in parsed.email.slice(0, 1)"
          :key="email"
          class="mb-1 text-fg-base-fg22"
        >
          {{ $t('common.email') }}:
          <a
            :href="`mailto:${email}`"
            class="text-comp-link-neut-def"
            target="_blank"
            @click.stop
          >
            {{ email }}
            <wui-icon
              name="linkM"
              small
              class="text-fg-base-fg22"
            />
          </a>
        </p>
        <a
          href="#"
          class="text-comp-link-neut-def"
          data-test="vcard-download-link"
          @click.prevent.stop="download(props.file, '/files/')"
        >
          {{ $t('mainAside.downloadVCard') }}
        </a>
      </div>

      <div class="mt-3 flex w-full items-stretch border-t border-bor-nav pt-2">
        <button
          type="button"
          class="min-w-0 flex-1 text-center text-label-m-13-18 text-comp-link-neut-def"
          data-test="vcard-write-button"
          @click.stop="openChat"
        >
          {{ $t('chat.write') }}
        </button>
        <slot
          name="add-to-chat"
          :p-served="contactImLogin"
        />
      </div>
    </div>
    <p
      v-else-if="loadError"
      class="text-label-r-13-18 text-fg-base-fg22"
    >
      {{ $t('mainAside.vcardLoadError') }}
      <a
        href="#"
        class="text-comp-link-neut-def"
        @click.prevent="download(props.file, '/files/')"
      >
        {{ props.file.filename || '' }}
      </a>
    </p>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { IMFile, isBotPServed } from '@wui/im'
import { computed, onMounted, ref } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'

import { resolveBotContact } from '@/shared/utils/contacts'
import { download } from '@/shared/utils/download-files'
import { createPrivateRoom } from '@/shared/utils/im'
import { optionalPush } from '@/shared/utils/router'
import { getAuthenticatedMediaUrl, getChatRoute } from '@/shared/utils/url'
import { normalizeImppToPServed, parseVCard, type ParsedVCard } from '@/shared/utils/vcard'

const props = defineProps<{
  file: IMFile
}>()

const parsed = ref<ParsedVCard | null>(null)
const loadError = ref(false)
const { findByInternalNumber, findByPServed } = useStoreContacts()

const fileUrl = computed(() => getAuthenticatedMediaUrl(`/files/${props.file.filecookie}`))

const internalNumbers = computed<string[]>(() => {
  if (!parsed.value) return []

  const sourceNumber = parsed.value.mobileTel[0] || parsed.value.tel[0] || ''
  const normalized = sourceNumber.replace(/[^\d+]/g, '')
  const digitsOnly = normalized.replace(/\D/g, '')

  return Array.from(new Set([sourceNumber, normalized, digitsOnly].filter(Boolean)))
})

const imppPServed = computed(() => {
  const impp = parsed.value?.impp[0]
  return impp ? normalizeImppToPServed(impp) : undefined
})

const contact = computed(() => {
  if (imppPServed.value) {
    const byImpp = findByPServed(imppPServed.value)
    if (byImpp) return byImpp
  }

  for (const number of internalNumbers.value) {
    const found = findByInternalNumber(number)
    if (found) return found
  }

  return undefined
})

const canOpenChat = computed(() => Boolean(contact.value?.imLogin || (imppPServed.value && isBotPServed(imppPServed.value))))

const contactImLogin = computed(() => contact.value?.imLogin)

const openChat = () => {
  if (!canOpenChat.value) {
    return
  }

  if (contact.value?.imLogin) {
    optionalPush(getChatRoute(contact.value))
    return
  }

  const pServed = imppPServed.value
  if (!pServed || !isBotPServed(pServed)) {
    return
  }

  const botContact = resolveBotContact(pServed, parsed.value?.name)
  createPrivateRoom(pServed, botContact)
  optionalPush(getChatRoute(botContact))
}

onMounted(async () => {
  try {
    const url = fileUrl.value
    const res = await fetch(url)
    if (!res.ok) {
      loadError.value = true
      return
    }
    const text = await res.text()
    parsed.value = parseVCard(text)
  } catch {
    loadError.value = true
  }
})
</script>
